import React, { useState, useContext, useEffect } from 'react';
import { useAllOrders } from '../../context/AllOrdersContext';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { useNotifications } from '../../context/NotificationContext';
import NotificationOverlay from '../../components/NotificationOverlay';
import { db } from '../../firebase/client';
import { collection, query, where, getDocs, doc, updateDoc, arrayRemove } from 'firebase/firestore';
import { DeliveryDriver, PlacedOrder } from '../../types';
import DriverRatingModal from '../../components/DriverRatingModal';
import NotificationService from '../../services/NotificationService';
import { formatDateReadable } from '../../utils/dateUtils';


const AdminOrdersScreen: React.FC = () => {
    const { placedOrders, updateOrderStatus, loading, respondToNudge } = useAllOrders();
    const { getRestaurantById, updateRestaurantDetails } = useData();
    const { currentUser } = useAuth();
    const { view } = useContext(AppContext);
    const { unreadCount, stopAlarm } = useNotifications();
    const [showNotifications, setShowNotifications] = useState(false);

    // Stop alarm when entering orders screen
    useEffect(() => {
        stopAlarm();
    }, []);

    // Initialisation intelligente basée sur les params (venant du dashboard par exemple)
    const [activeTab, setActiveTab] = useState<'pending' | 'in_progress' | 'sent' | 'completed' | 'cancelled'>(
        view.params?.initialTab || 'pending'
    );
    const [selectedOrderForRating, setSelectedOrderForRating] = useState<PlacedOrder | null>(null);
    const [showRatingModal, setShowRatingModal] = useState(false);

    // Effet pour mettre à jour l'onglet si on navigue à nouveau depuis le dashboard
    useEffect(() => {
        if (view.params?.initialTab) {
            setActiveTab(view.params.initialTab);
            // Also stop alarm if navigated from dashboard
            stopAlarm();
        }
    }, [view.params?.initialTab]);

    // force re-render logic removed as timers are no longer used

    // State pour le tri
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [viewMode, setViewMode] = useState<'list' | 'management'>('list');
    const [selectedOrderForDriver, setSelectedOrderForDriver] = useState<string | null>(null);

    // --- AUTO-CLEANUP EFFECT REMOVED AT USER REQUEST ---
    const [acceptingOrderId, setAcceptingOrderId] = useState<string | null>(null);

    const [onlineDrivers, setOnlineDrivers] = useState<DeliveryDriver[]>([]);
    const [invalidDrivers, setInvalidDrivers] = useState<string[]>([]);
    const [fetchingDrivers, setFetchingDrivers] = useState(false);

    const [manualDriverName, setManualDriverName] = useState('');
    const [manualDriverPhone, setManualDriverPhone] = useState('');
    const [selectedNote, setSelectedNote] = useState<{ id: string, comment: string } | null>(null);

    // Fetch online drivers when modal opens
    React.useEffect(() => {
        if (!selectedOrderForDriver) return;

        const fetchOnlineDrivers = async () => {
            setFetchingDrivers(true);
            try {
                // 1. Fetch online drivers in the same wilaya as the restaurant
                const usersRef = collection(db, 'users');
                const restaurantWilaya = currentRestaurant?.wilaya;

                let q = query(usersRef, where('role', '==', 'livreur'), where('isOnline', '==', true));
                
                if (restaurantWilaya) {
                    q = query(q, where('wilaya', '==', restaurantWilaya));
                }

                const snapshot = await getDocs(q);

                const drivers: DeliveryDriver[] = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        uid: doc.id,
                        id: doc.id,
                        name: data.name || 'Livreur',
                        phone: data.phone || '',
                        rating: data.rating,
                        ratingCount: data.ratingCount,
                        restaurantRating: data.restaurantRating,
                        restaurantRatingCount: data.restaurantRatingCount,
                        deliveredOrdersCount: data.deliveredOrdersCount,
                        isManual: false
                    };
                });
                setOnlineDrivers(drivers);

                // 2. Verify habitual drivers existence
                const available = currentRestaurant?.deliveryDrivers || [];
                const invalidIds: string[] = [];

                const checkPromises = available.map(async (d) => {
                    // If they are online, they exist. Otherwise check Firestore.
                    if (drivers.some(od => od.id === d.id)) return;

                    // Efficiently check existence using getDocs with ID filter
                    const dSnap = await getDocs(query(usersRef, where('__name__', '==', d.id)));
                    if (dSnap.empty) {
                        invalidIds.push(d.id);
                    }
                });

                await Promise.all(checkPromises);
                setInvalidDrivers(invalidIds);

            } catch (err) {
                console.error("Erreur récupération livreurs:", err);
            } finally {
                setFetchingDrivers(false);
            }
        };

        fetchOnlineDrivers();
    }, [selectedOrderForDriver]);

    // Récupérer le restaurant actuel pour avoir la liste des livreurs
    const currentRestaurant = currentUser?.restaurantId ? getRestaurantById(currentUser.restaurantId) : null;
    const availableDrivers = (currentRestaurant?.deliveryDrivers || []).filter(d => !invalidDrivers.includes(d.id));
    const ghostDrivers = (currentRestaurant?.deliveryDrivers || []).filter(d => invalidDrivers.includes(d.id));

    const handleAcceptOrder = (order: any) => {
        // Stop alarm immediately on interaction
        stopAlarm();
        // Peu importe le type, on passe directement en préparation
        updateOrderStatus(order.id, 'En préparation');
    };

    const confirmDriverAssignment = (driver: DeliveryDriver) => {
        if (selectedOrderForDriver) {
            // Assigner le livreur et rester en préparation
            updateOrderStatus(selectedOrderForDriver, 'En préparation', { ...driver, isManual: false });
            setSelectedOrderForDriver(null);
        }
    };

    const handleRemoveHabitualDriver = async (driver: DeliveryDriver) => {
        if (!currentUser?.restaurantId) return;
        if (!window.confirm(`Retirer ${driver.name} de vos livreurs habituels ?`)) return;

        try {
            const restRef = doc(db, 'restaurants', currentUser.restaurantId);
            await updateDoc(restRef, {
                deliveryDrivers: arrayRemove(driver)
            });
            // Update local UI immediately if needed, though getRestaurantById should reflect it
            alert("Livreur retiré.");
        } catch (err) {
            console.error(err);
            alert("Erreur lors du retrait.");
        }
    };

    // Sort orders based on sortOrder
    const sortedOrders = [...placedOrders].sort((a, b) => {
        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    const filteredOrders = sortedOrders.filter(order => {
        if (activeTab === 'pending') return order.status === 'En attente';
        if (activeTab === 'in_progress') return order.status === 'En préparation' || order.status === 'Prête';
        if (activeTab === 'sent') return order.status === 'Envoyée';
        if (activeTab === 'completed') return order.status === 'Terminée' || order.status === 'Livrée';
        if (activeTab === 'cancelled') return order.status === 'Annulée' || order.status === 'Refusée';
        return false;
    });

    // Count for badges
    const pendingCount = placedOrders.filter(o => o.status === 'En attente').length;
    const inProgressCount = placedOrders.filter(o => o.status === 'En préparation' || o.status === 'Prête').length;
    const sentCount = placedOrders.filter(o => o.status === 'Envoyée').length;
    const completedCount = placedOrders.filter(o => o.status === 'Terminée' || o.status === 'Livrée').length;
    const cancelledCount = placedOrders.filter(o => o.status === 'Annulée' || o.status === 'Refusée').length;

    if (loading) {
        return (
            <div className="p-4 bg-gray-50 min-h-screen">
                <header className="py-4">
                <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">Liste des Commandes</h1>
                <p className="text-gray-600">Chargement...</p>
            </header>
            </div>
        )
    }

    return (
        <div className="p-4 bg-gray-50 min-h-screen pb-24 relative">
            <header className="py-6 flex justify-between items-start mb-4">
                <div className="flex-1">
                    <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">Commandes</h1>
                    <p className="text-gray-500 font-black text-[10px] uppercase tracking-[0.2em] italic mt-1 opacity-70">Gérez vos flux en temps réel</p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="p-2.5 rounded-xl bg-white shadow-sm border border-gray-100 text-gray-600 hover:bg-gray-50 transition-all relative"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white border-2 border-white shadow-sm">
                                    {unreadCount}
                                </span>
                            )}
                        </button>
                        <NotificationOverlay
                            isOpen={showNotifications}
                            onClose={() => setShowNotifications(false)}
                            color="bg-green-600"
                        />
                    </div>

                    <button
                        onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <span>{sortOrder === 'desc' ? 'Plus récent' : 'Plus ancien'}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>
            </header>

            {/* Tabs for Filtering - Premium Redesign */}
            <div className="flex flex-col gap-4 mb-8">
                <div className="flex space-x-2 bg-white p-1 rounded-[1.2rem] shadow-[0_8px_20px_-5px_rgba(0,0,0,0.05)] overflow-x-auto no-scrollbar scroll-smooth border border-gray-100">
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`whitespace-nowrap py-2.5 px-4 rounded-xl text-[9px] uppercase italic tracking-[0.1em] font-black flex items-center justify-center gap-2 transition-all min-w-[100px] ${activeTab === 'pending' ? 'bg-red-600 text-white shadow-lg z-10' : 'text-gray-400 hover:bg-gray-50'}`}
                    >
                        <span>En attente</span>
                        {pendingCount > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black ${activeTab === 'pending' ? 'bg-white text-red-600' : 'bg-gray-100 text-gray-400'}`}>{pendingCount}</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('in_progress')}
                        className={`whitespace-nowrap py-2.5 px-4 rounded-xl text-[9px] uppercase italic tracking-[0.1em] font-black flex items-center justify-center gap-2 transition-all min-w-[100px] ${activeTab === 'in_progress' ? 'bg-red-600 text-white shadow-lg z-10' : 'text-gray-400 hover:bg-gray-50'}`}
                    >
                        <span>En cours</span>
                        {inProgressCount > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black ${activeTab === 'in_progress' ? 'bg-white text-red-600' : 'bg-gray-100 text-gray-400'}`}>{inProgressCount}</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('sent')}
                        className={`whitespace-nowrap py-2.5 px-4 rounded-xl text-[9px] uppercase italic tracking-[0.1em] font-black flex items-center justify-center gap-2 transition-all min-w-[100px] ${activeTab === 'sent' ? 'bg-red-600 text-white shadow-lg z-10' : 'text-gray-400 hover:bg-gray-50'}`}
                    >
                        <span>Envoyée</span>
                        {sentCount > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black ${activeTab === 'sent' ? 'bg-white text-red-600' : 'bg-gray-100 text-gray-400'}`}>{sentCount}</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('completed')}
                        className={`whitespace-nowrap py-2.5 px-4 rounded-xl text-[9px] uppercase italic tracking-[0.1em] font-black flex items-center justify-center gap-2 transition-all min-w-[100px] ${activeTab === 'completed' ? 'bg-red-600 text-white shadow-lg z-10' : 'text-gray-400 hover:bg-gray-50'}`}
                    >
                        <span>Terminée</span>
                        {completedCount > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black ${activeTab === 'completed' ? 'bg-white text-red-600' : 'bg-gray-100 text-gray-400'}`}>{completedCount}</span>}
                    </button>
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={() => setActiveTab('cancelled')}
                        className={`py-1.5 px-4 rounded-full text-xs font-bold flex items-center gap-2 transition-all border ${activeTab === 'cancelled' ? 'bg-red-50 border-red-200 text-red-600 shadow-sm' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'}`}
                    >
                        <span>Historique Annulées</span>
                        {cancelledCount > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === 'cancelled' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'}`}>{cancelledCount}</span>}
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {filteredOrders.length > 0 ? (
                    filteredOrders.map(order => (
                        <div key={order.id} className={`bg-white p-7 rounded-[2.5rem] shadow-[0_15px_35px_-10px_rgba(0,0,0,0.1)] border border-gray-100 transition-all hover:shadow-[0_20px_45px_-12px_rgba(0,0,0,0.15)] group relative overflow-hidden ${order.status === 'Annulée' || order.status === 'Refusée' ? 'opacity-70 grayscale' : ''}`}>
                            <div className="flex justify-between items-start mb-5 relative z-10">
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter italic group-hover:text-red-600 transition-colors">{order.customerName}</h2>
                                        <a href={`tel:${order.customerPhone}`} className="bg-red-50 p-2 rounded-full text-red-600 hover:bg-red-100 hover:scale-110 transition-all active:scale-90 shadow-sm border border-red-100 flex items-center justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                            </svg>
                                        </a>
                                    </div>
                                    <div className="flex flex-col gap-2 mt-4">
                                        {order.customerAddress && (
                                            <p className="text-[10px] text-gray-400 font-black flex items-start gap-2 uppercase tracking-wide italic max-w-[200px]">
                                                <span className="text-sm grayscale opacity-50">📍</span>
                                                {order.customerAddress}
                                            </p>
                                        )}
                                        <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] italic">
                                            <span>🕒</span> 
                                            {formatDateReadable(order.timestamp)}
                                        </p>
                                    </div>
                                </div>
                                <span className={`px-5 py-2 text-[10px] font-black uppercase rounded-full shadow-sm tracking-[0.1em] italic border ${order.status === 'En attente' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                    order.status === 'En préparation' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                        order.status === 'Envoyée' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                                            order.status === 'Livrée' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                                order.status === 'Prête' ? 'bg-red-600 text-white border-red-600' :
                                                    order.status === 'Terminée' ? 'bg-green-50 text-green-700 border-green-100' :
                                                        order.status === 'Annulée' ? 'bg-gray-100 text-gray-700 border-gray-200' : 'bg-red-50 text-red-700 border-red-100'
                                    }`}>{order.status}</span>
                            </div>

                            {/* Delivery Type Badge */}
                            <div className="mb-2 flex items-center justify-between">
                                <span className="px-3 py-1 bg-red-600 text-white text-[9px] font-black uppercase rounded-full shadow-sm tracking-wider italic">
                                    {order.deliveryType === "takeaway" ? "À emporter" : "Livraison"}
                                </span>

                                {["Livrée", "Terminée"].includes(order.status) && (
                                    <div className="flex flex-col gap-2">
                                        {order.restaurantRating !== undefined && (
                                            <div className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded-lg border border-red-100 shadow-sm self-start">
                                                <span className="text-red-600 text-[10px]">⭐</span>
                                                <span className="text-red-700 text-xs font-black italic">{order.restaurantRating.toFixed(1)}</span>
                                                <span className="text-[8px] text-red-400 font-black uppercase tracking-tighter italic">(Note Client)</span>
                                            </div>
                                        )}
                                        {order.driverRating && (
                                            <div className="flex items-center gap-1 bg-yellow-50 px-2 py-0.5 rounded border border-yellow-100 shadow-sm self-start">
                                                <span className="text-yellow-500 text-xs text-[10px]">⭐</span>
                                                <span className="text-yellow-700 text-xs font-bold">{order.driverRating}</span>
                                                <span className="text-[8px] text-yellow-600">(Note Livreur)</span>
                                            </div>
                                        )}
                                        {order.restaurantDriverRating !== undefined && (
                                            <div className="flex items-center gap-1 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 shadow-sm self-start">
                                                <span className="text-blue-500 text-xs text-[10px]">⭐</span>
                                                <span className="text-blue-700 text-xs font-bold">{order.restaurantDriverRating}</span>
                                                <span className="text-[8px] text-blue-600">(Ma Note Livreur)</span>
                                            </div>
                                        )}
                                        {order.restaurantDriverRating === undefined && order.livreurId && !order.livreurId.startsWith("manual_") && (
                                            <button 
                                                onClick={() => {
                                                    setSelectedOrderForRating(order);
                                                    setShowRatingModal(true);
                                                }}
                                                className="flex items-center gap-1.5 bg-blue-50 px-3 py-1 rounded-lg border border-blue-100 shadow-sm self-start text-blue-700 hover:bg-blue-100 transition-all active:scale-95 group"
                                            >
                                                <span className="text-blue-500 text-[10px] group-hover:scale-125 transition-transform">⭐</span>
                                                <span className="text-[9px] font-black uppercase tracking-[0.05em] italic">Noter le livreur</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100 mt-5">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 italic">Contenu Préparation</p>
                                <ul className="space-y-4">
                                    {order.items.map((item: any, idx: number) => {
                                        const isPack = item.type === 'pack';
                                        const itemName = isPack ? (item.pack?.name || 'Pack') : (item.dish?.name || 'Plat');
                                        const itemPrice = isPack ? (item.pack?.packPrice || 0) : (item.dish?.price || 0);
                                        
                                        return (
                                            <li key={idx} className="flex flex-col border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                                                <div className="flex justify-between items-baseline">
                                                    <p className="text-sm font-black text-gray-900 uppercase tracking-tighter italic leading-tight">
                                                        <span className="text-red-600 mr-2">{item.quantity}x</span> {itemName}
                                                    </p>
                                                    <span className="text-xs font-black text-gray-400 italic shrink-0 ml-4">{itemPrice * item.quantity} DA</span>
                                                </div>
                                                
                                                {isPack && item.pack?.dishes && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {item.pack.dishes.map((d: any, dIdx: number) => (
                                                            <span key={dIdx} className="text-[9px] font-black text-gray-500 bg-white border border-gray-100 px-2 py-0.5 rounded-full uppercase italic tracking-tight">
                                                                {d.quantity && d.quantity > 1 ? `${d.quantity}x ` : ''}{d.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}

                                                {item.selectedExtras && item.selectedExtras.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {item.selectedExtras.map((extra: any) => (
                                                            <span key={extra.id} className="text-[9px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-full uppercase italic tracking-tight">
                                                                + {extra.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="border-t border-dashed border-gray-200 mt-4 pt-4 flex justify-between items-center text-[10px] font-black uppercase tracking-widest italic text-gray-400">
                                    <span>Frais de Livraison</span>
                                    <span className="text-gray-900">{order.deliveryFee > 0 ? `${order.deliveryFee} DA` : 'Gratuit'}</span>
                                </div>
                                {order.assignedDriver && (
                                    <div className="mt-4 bg-red-600 text-white p-4 rounded-2xl flex items-center justify-between border border-white/10 shadow-xl relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-full -mr-8 -mt-8" />
                                        <div className="flex items-center gap-3 relative z-10">
                                            <span className="text-xl">🛵</span>
                                            <div>
                                                <p className="text-[8px] font-black text-red-100 uppercase tracking-widest italic mb-0.5 opacity-80">Livreur Assigné</p>
                                                <p className="text-xs font-black uppercase italic tracking-tighter">{order.assignedDriver.name}</p>
                                            </div>
                                        </div>
                                        <a href={`tel:${order.assignedDriver.phone}`} className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl active:scale-90 transition-all relative z-10">
                                            <span className="text-sm text-red-600">📞</span>
                                        </a>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-3 pt-2">
                                <div className="flex justify-between items-center bg-red-600 text-white p-5 rounded-[1.5rem] shadow-xl relative overflow-hidden group/total">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-8 -mt-8 transition-transform group-hover/total:scale-110" />
                                    <div>
                                        <p className="text-[10px] font-black text-red-100 uppercase tracking-[0.2em] italic mb-1">Total Recettes</p>
                                        <p className="font-black text-2xl italic tracking-tighter text-white">{order.totalPrice} <span className="text-xs text-red-200">DA</span></p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black text-red-100 uppercase tracking-widest italic mb-1">Statut</p>
                                        <p className={`text-[10px] font-black uppercase italic ${order.status === 'Terminée' ? 'text-green-300' : 'text-amber-300'}`}>{order.status}</p>
                                    </div>
                                </div>
                                
                                <div className="flex gap-2">
                                    {order.comment && ['En attente', 'En préparation', 'Prête', 'Envoyée'].includes(order.status) && (
                                        <button 
                                            onClick={() => setSelectedNote({ id: order.id, comment: order.comment! })}
                                            className="px-4 py-2 bg-amber-50 text-amber-600 border border-amber-200 rounded-lg font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95"
                                        >
                                            <span>📝</span> Note
                                        </button>
                                    )}

                                    {order.status === 'En attente' && (
                                        <div className="flex flex-col gap-3 w-full">
                                            {acceptingOrderId === order.id ? (
                                                <div className="flex flex-col gap-4 bg-amber-50/50 p-5 rounded-[2rem] border border-amber-100 animate-fade-in text-center relative overflow-hidden backdrop-blur-sm">
                                                    <div className="absolute top-0 right-0 w-16 h-16 bg-amber-200/20 rounded-full -mr-8 -mt-8" />
                                                    {order.comment && (
                                                        <button
                                                            onClick={() => setSelectedNote({ id: order.id, comment: order.comment! })}
                                                            className="mb-1 p-3 bg-white rounded-2xl border-2 border-amber-400 shadow-xl shadow-amber-200/50 animate-bounce flex items-center justify-center gap-3 hover:bg-amber-50 transition-all active:scale-95"
                                                        >
                                                            <span className="text-base text-amber-600 italic font-black">📝 NOTE CLIENT</span>
                                                        </button>
                                                    )}
                                                    <p className="text-[10px] font-black text-amber-800 uppercase tracking-[0.2em] italic mb-1">Temps de préparation ?</p>
                                                    <div className="grid grid-cols-4 gap-2">
                                                        {[15, 30, 45, 60].map(time => (
                                                            <button
                                                                key={time}
                                                                onClick={() => {
                                                                    updateOrderStatus(order.id, 'En préparation', undefined, time);
                                                                    setAcceptingOrderId(null);
                                                                    setActiveTab('in_progress');
                                                                }}
                                                                className="py-3.5 bg-white border-2 border-amber-200 rounded-xl text-xs font-black text-amber-700 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all active:scale-90 shadow-sm"
                                                            >
                                                                {time}'
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <button onClick={() => setAcceptingOrderId(null)} className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-2 hover:text-red-600 transition-colors italic leading-relaxed">Annuler</button>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-2 gap-3 w-full">
                                                    <button onClick={() => setAcceptingOrderId(order.id)} className="py-4 bg-red-600 text-white rounded-2xl font-black text-[10px] uppercase italic tracking-[0.2em] hover:scale-105 active:scale-95 transition-all shadow-xl shadow-red-50 flex items-center justify-center gap-2">
                                                       <span>✓</span> Accepter
                                                    </button>
                                                    <button onClick={() => window.confirm("Refuser cette commande ?") && updateOrderStatus(order.id, 'Refusée')} className="py-4 bg-gray-100 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-2xl font-black text-[10px] uppercase italic tracking-[0.2em] transition-all">
                                                       <span>✕</span> Refuser
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {order.status === 'En préparation' && (
                                        <div className="flex flex-col gap-3 w-full">
                                            {/* Premium Nudge Alert */}
                                            {(order.nudgeCount || 0) > 0 && !order.nudgeResponse && (
                                                <div className="bg-red-50/50 border-2 border-red-200 rounded-[2rem] p-5 mb-3 animate-pulse shadow-xl shadow-red-100/50 relative overflow-hidden backdrop-blur-sm">
                                                    <div className="absolute -right-8 -top-8 w-24 h-24 bg-red-200/20 rounded-full" />
                                                    <div className="flex items-center gap-4 mb-3 relative z-10">
                                                        <span className="text-2xl">⚡</span>
                                                        <div className="flex-1">
                                                            <p className="text-[10px] font-black text-red-600 uppercase tracking-[0.2em] leading-none mb-1 italic">Vite ! Relance Client</p>
                                                            <p className="text-xs font-black text-red-900 uppercase italic tracking-tighter">Le client attend sa commande ({order.nudgeCount} relances)</p>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-3 gap-2 relative z-10">
                                                        <button 
                                                            onClick={() => respondToNudge(order.id, 'coming')}
                                                            className="py-3 bg-red-600 text-white text-[9px] font-black uppercase rounded-xl shadow-lg shadow-red-200 active:scale-95 transition-all italic tracking-widest"
                                                        >
                                                            Arrive !
                                                        </button>
                                                        <button 
                                                            onClick={() => respondToNudge(order.id, 'delayed', 10)}
                                                            className="py-3 bg-red-600 text-white text-[9px] font-black uppercase rounded-xl shadow-lg active:scale-95 transition-all italic tracking-widest"
                                                        >
                                                            +10m
                                                        </button>
                                                        <button 
                                                            onClick={() => respondToNudge(order.id, 'delayed', 20)}
                                                            className="py-3 bg-gray-200 text-gray-500 text-[9px] font-black uppercase rounded-xl active:scale-95 transition-all italic tracking-widest"
                                                        >
                                                            +20m
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {(order.deliveryType === 'takeaway' || currentRestaurant?.deliveryEnabled === false) ? (
                                                <div className="flex flex-col gap-3 w-full">
                                                    <button 
                                                        onClick={() => updateOrderStatus(order.id, 'Prête')} 
                                                        className="w-full py-5 bg-red-600 text-white rounded-[1.5rem] font-black text-[10px] uppercase italic tracking-[0.2em] shadow-xl shadow-red-100 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3 animate-pulse"
                                                    >
                                                        <span className="text-sm">🔥</span> COMMANDE PRÊTE <span>{order.deliveryType === 'takeaway' ? '🛍️' : '📦'}</span>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-3 w-full">
                                                    {!order.assignedDriver ? (
                                                        <div className="flex flex-col gap-3 w-full">
                                                            <button 
                                                                onClick={() => setSelectedOrderForDriver(order.id)} 
                                                                className="w-full py-5 bg-red-600 text-white rounded-[1.5rem] font-black text-[10px] uppercase italic tracking-[0.2em] shadow-xl shadow-red-50 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                                                            >
                                                                <span>🛵</span> Assigner un livreur
                                                            </button>
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <button onClick={() => window.confirm("Envoyer sans livreur ?") && updateOrderStatus(order.id, 'Envoyée')} className="py-4 bg-gray-100 text-gray-400 font-black text-[9px] uppercase italic tracking-widest rounded-2xl hover:text-black transition-all">Sans livreur</button>
                                                                <button onClick={() => window.confirm("Annuler cette commande ?") && updateOrderStatus(order.id, 'Refusée')} className="py-4 bg-red-50 text-red-400 font-black text-[9px] uppercase italic tracking-widest rounded-2xl border border-red-100">Annuler</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col gap-3 w-full">
                                                            <div className="grid grid-cols-3 gap-3">
                                                                <button onClick={() => setSelectedOrderForDriver(order.id)} className="col-span-1 py-4 bg-gray-100 text-gray-500 font-black text-[9px] uppercase italic tracking-widest rounded-2xl active:scale-95 transition-all">Changer</button>
                                                                <button
                                                                    onClick={() => updateOrderStatus(order.id, 'Envoyée', order.assignedDriver)}
                                                                    disabled={order.livreurStatus === 'pending' || order.livreurStatus === 'rejected'}
                                                                    className={`col-span-2 py-4 rounded-2xl font-black text-[9px] uppercase italic tracking-[0.1em] shadow-xl transition-all ${order.livreurStatus === 'accepted' ? 'bg-red-600 text-white shadow-red-50 hover:scale-[1.02] active:scale-95' : 'bg-gray-200 text-gray-400'}`}
                                                                >
                                                                    {order.livreurStatus === 'accepted' ? 'Expédier la commande ✓' :
                                                                        order.livreurStatus === 'rejected' ? 'Refusé par livreur' : 'Attente Livreur...'}
                                                                </button>
                                                            </div>
                                                            <button 
                                                                onClick={() => window.confirm("Annuler cette commande ?") && updateOrderStatus(order.id, 'Refusée')} 
                                                                className="w-full py-2 text-[9px] font-black text-gray-400 uppercase italic tracking-widest hover:text-red-500 transition-colors"
                                                            >
                                                                Annuler la Commande
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {order.status === 'Prête' && (
                                        <button 
                                            onClick={() => updateOrderStatus(order.id, 'Terminée')} 
                                            className="w-full py-5 bg-red-600 text-white rounded-[1.5rem] font-black text-[10px] uppercase italic tracking-[0.2em] shadow-xl shadow-red-50 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3"
                                        >
                                            <span className="text-sm">💰</span> CLIENT A RÉCUPÉRÉ <span>🛍️</span>
                                        </button>
                                    )}
                                    {order.status === 'Annulée' && (
                                        <div className="py-4 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center gap-2">
                                            <span className="text-[10px] font-black text-gray-400 uppercase italic tracking-widest leading-none">Commande annulée par le client</span>
                                        </div>
                                    )}
                                    {order.status === 'Refusée' && (
                                        <div className="py-4 bg-red-50/50 border border-red-100 rounded-2xl flex items-center justify-center gap-2">
                                            <span className="text-[10px] font-black text-red-400 uppercase italic tracking-widest leading-none">Commande refusée par restaurant</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-10 bg-white rounded-xl shadow-sm">
                        <p className="text-gray-500">Aucune commande dans cette catégorie.</p>
                    </div>
                )}
            </div>

            {/* Modal de sélection de livreur */}
            {selectedOrderForDriver && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-scale-in">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="font-bold text-lg text-gray-800">Assigner un livreur</h3>
                            <button onClick={() => setSelectedOrderForDriver(null)} className="text-gray-400 hover:text-gray-600">✕</button>
                        </div>
                        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-6">
                            {fetchingDrivers ? (
                                <div className="text-center py-8 text-gray-400">Recherche de livreurs en ligne...</div>
                            ) : (
                                <>
                                    {/* Livreur Interne / Manuel */}
                                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 mb-6">
                                        <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <span>👤</span> Livreur Interne (Mon staff)
                                        </h4>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                placeholder="Nom du livreur..."
                                                value={manualDriverName}
                                                onChange={(e) => setManualDriverName(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                                            />
                                            <input
                                                type="tel"
                                                placeholder="Téléphone..."
                                                value={manualDriverPhone}
                                                onChange={(e) => setManualDriverPhone(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                                            />
                                            <button
                                                onClick={() => {
                                                    if (!manualDriverName || !manualDriverPhone) {
                                                        alert("Veuillez saisir le nom et le téléphone.");
                                                        return;
                                                    }
                                                    updateOrderStatus(selectedOrderForDriver!, 'En préparation', {
                                                        id: `manual_${manualDriverPhone.replace(/\s+/g, '')}`,
                                                        name: manualDriverName,
                                                        phone: manualDriverPhone,
                                                        isManual: true
                                                    });
                                                    setSelectedOrderForDriver(null);
                                                    setManualDriverName('');
                                                    setManualDriverPhone('');
                                                }}
                                                className="w-full py-2 bg-amber-600 text-white font-bold rounded-lg text-xs hover:bg-amber-700 shadow-sm transition-all active:scale-95"
                                            >
                                                Assigner mon livreur 👤
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mes Livreurs Habituels */}
                                    <div>
                                        <h4 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <span>⭐</span> Mes Livreurs Habituels
                                        </h4>
                                        <div className="grid grid-cols-1 gap-3">
                                            {availableDrivers.length === 0 && ghostDrivers.length === 0 ? (
                                                <p className="text-xs text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-dashed border-gray-200 text-center">Aucun livreur habituel enregistré.</p>
                                            ) : (
                                                <div className="space-y-3">
                                                    {availableDrivers.map(driver => {
                                                        const onlineMatch = onlineDrivers.find(od => od.id === driver.id);
                                                        const isOnline = !!onlineMatch;
                                                        
                                                        // Merge latest stats if online, otherwise use stored data
                                                        const rating = onlineMatch?.rating ?? driver.rating;
                                                        const restaurantRating = onlineMatch?.restaurantRating ?? driver.restaurantRating;
                                                        const deliveredOrdersCount = onlineMatch?.deliveredOrdersCount ?? driver.deliveredOrdersCount;

                                                        return (
                                                            <div 
                                                                key={driver.id} 
                                                                className={`relative p-4 rounded-2xl border transition-all shadow-sm ${isOnline ? 'bg-white border-blue-100 hover:border-blue-300' : 'bg-gray-50 border-gray-100 opacity-60'}`}
                                                            >
                                                                <button
                                                                    onClick={() => handleRemoveHabitualDriver(driver)}
                                                                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs hover:bg-red-200 shadow-sm z-10"
                                                                    title="Retirer des habituels"
                                                                >
                                                                    ✕
                                                                </button>

                                                                 <div className="absolute top-3 right-6 flex flex-col items-end gap-1">
                                                                    <div className="flex items-center gap-2">
                                                                        {rating !== undefined && (
                                                                            <div className="flex items-center gap-0.5 bg-yellow-50 px-1.5 py-0.5 rounded text-[9px] font-bold text-yellow-700 border border-yellow-100" title="Note Clients">
                                                                                <span>⭐</span> {(rating || 0).toFixed(1)}
                                                                            </div>
                                                                        )}
                                                                        {restaurantRating !== undefined && (
                                                                            <div className="flex items-center gap-0.5 bg-blue-50 px-1.5 py-0.5 rounded text-[9px] font-bold text-blue-700 border border-blue-100" title="Note Restaurant">
                                                                                <span>🍴</span> {(restaurantRating || 0).toFixed(1)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {deliveredOrdersCount !== undefined && (
                                                                        <div className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                                                            📦 {deliveredOrdersCount} courses
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="flex items-center gap-3 mb-4">
                                                                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
                                                                    <div>
                                                                        <p className="font-black text-gray-900 text-lg leading-tight">{driver.name}</p>
                                                                        <p className="text-xs text-blue-600 font-medium">{driver.phone}</p>
                                                                    </div>
                                                                </div>

                                                                <button
                                                                    onClick={() => isOnline && confirmDriverAssignment(driver)}
                                                                    disabled={!isOnline}
                                                                    className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${isOnline ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md active:scale-95' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                                                                >
                                                                    {isOnline ? 'Assigner ce livreur 🛵' : 'Hors ligne'}
                                                                </button>
                                                            </div>
                                                        );
                                                    })}

                                                    {ghostDrivers.map(driver => (
                                                        <div key={driver.id} className="p-3 rounded-xl border border-red-100 bg-red-50/50 flex items-center justify-between opacity-80">
                                                            <div>
                                                                <p className="text-sm font-bold text-red-900">{driver.name}</p>
                                                                <p className="text-[10px] text-red-600 font-medium">Compte supprimé ou invalide</p>
                                                            </div>
                                                            <button
                                                                onClick={() => handleRemoveHabitualDriver(driver)}
                                                                className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-[10px] font-bold hover:bg-red-200"
                                                            >
                                                                Supprimer
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Autres Livreurs en Ligne */}
                                    <div>
                                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <span>🛵</span> Autres Livreurs en Ligne
                                        </h4>
                                        <div className="grid grid-cols-1 gap-3">
                                            {onlineDrivers.filter(od => !availableDrivers.some(ad => ad.id === od.id)).length === 0 ? (
                                                <p className="text-xs text-gray-400 italic text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 w-full">Aucun autre livreur disponible.</p>
                                            ) : (
                                                onlineDrivers
                                                    .filter(od => !availableDrivers.some(ad => ad.id === od.id))
                                                    .map(driver => (
                                                        <div
                                                            key={driver.id}
                                                            className="relative p-4 rounded-2xl border border-gray-100 transition-all shadow-sm bg-white hover:border-blue-200"
                                                        >
                                                            {/* Badge en haut à droite: Note et Courses */}
                                                            <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                                                                <div className="flex items-center gap-2">
                                                                    {driver.rating !== undefined && (
                                                                        <div className="flex items-center gap-0.5 bg-yellow-50 px-1.5 py-0.5 rounded text-[9px] font-bold text-yellow-700 border border-yellow-100" title="Note Clients">
                                                                            <span>⭐</span> {(driver.rating || 0).toFixed(1)}
                                                                        </div>
                                                                    )}
                                                                    {driver.restaurantRating !== undefined && (
                                                                        <div className="flex items-center gap-0.5 bg-blue-50 px-1.5 py-0.5 rounded text-[9px] font-bold text-blue-700 border border-blue-100" title="Note Restaurant">
                                                                            <span>🍴</span> {(driver.restaurantRating || 0).toFixed(1)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {driver.deliveredOrdersCount !== undefined && (
                                                                    <div className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                                                        📦 {driver.deliveredOrdersCount} courses
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.5)] flex-shrink-0"></div>
                                                                <div>
                                                                    <p className="font-black text-gray-900 text-lg leading-tight">{driver.name}</p>
                                                                    <p className="text-xs text-blue-600 font-medium">{driver.phone}</p>
                                                                </div>
                                                            </div>

                                                            <button
                                                                onClick={() => confirmDriverAssignment(driver)}
                                                                className="w-full py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold shadow-md hover:bg-blue-700 transition-all active:scale-95"
                                                            >
                                                                Assigner ce livreur 🛵
                                                            </button>
                                                        </div>
                                                    ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Popup Note Client */}
            {selectedNote && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-6 animate-fade-in" onClick={() => setSelectedNote(null)}>
                    <div
                        className="bg-white w-full max-w-xs rounded-3xl shadow-2xl overflow-hidden animate-pop-in border border-gray-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gray-50 p-4 flex justify-between items-center border-b border-gray-100">
                            <h3 className="font-black text-gray-900 uppercase tracking-tighter flex items-center gap-2">
                                <span>📝</span> Note Client
                            </h3>
                            <button onClick={() => setSelectedNote(null)} className="w-8 h-8 rounded-full bg-gray-900/10 flex items-center justify-center text-gray-900 font-bold hover:bg-gray-900/20 transition-colors">✕</button>
                        </div>
                        <div className="p-8">
                            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 max-h-[40vh] overflow-y-auto custom-scrollbar">
                                <p className="text-lg font-bold text-gray-800 italic text-center whitespace-pre-wrap">
                                    "{selectedNote.comment}"
                                </p>
                            </div>
                            <button
                                onClick={() => setSelectedNote(null)}
                                className="w-full mt-6 py-4 bg-red-600 text-white font-black rounded-2xl shadow-xl shadow-red-100 hover:bg-red-700 transition-all active:scale-95 uppercase tracking-widest text-sm"
                            >
                                Compris !
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal de notation du livreur par le restaurant */}
            {showRatingModal && selectedOrderForRating && (
                <DriverRatingModal
                    order={selectedOrderForRating}
                    isRestaurant={true}
                    onClose={() => {
                        setShowRatingModal(false);
                        setSelectedOrderForRating(null);
                    }}
                    onSuccess={() => {
                        setShowRatingModal(false);
                        setSelectedOrderForRating(null);
                    }}
                />
            )}
        </div>
    );
};

export default AdminOrdersScreen;
